import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `documents:` scope (F1/F15), following the
 * exact `web:` precedent (`apps/web/src/i18n/webCatalog.ts` + `useWebT.ts`)
 * — NOT a second i18n runtime (E18/§13.3 ban), just physically scoped here
 * because `packages/i18n/**` is outside this task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/documents.json`.
 */
const en = {
  'list.eyebrow': 'Your library',
  'list.title': 'Documents',
  'list.hint.label': 'Tip:',
  'list.hint.text': 'press {shortcut} anywhere to jump to a document or run an action.',
  'list.hint.dismiss': 'Don’t show again',
  'list.hint.close': 'Dismiss tip',
  'list.newDoc': 'New document',
  'list.searchLabel': 'Search documents',
  'list.searchPlaceholder': 'Search by title…',
  'list.sort.label': 'Sort by',
  'list.sort.updatedAt-desc': 'Recently updated',
  'list.sort.createdAt-desc': 'Recently created',
  'list.sort.title-asc': 'Title A–Z',
  'list.sort.status-asc': 'Status',
  'list.filter.label': 'Filter by status',
  'list.filter.all': 'All',
  'list.filter.ready': 'Ready',
  'list.filter.generating': 'Generating',
  'list.filter.draft': 'Drafts',
  'list.empty.title': 'No documents yet',
  'list.empty.description': 'Generate, expand, and share AI-enhanced documents — start with your first one.',
  'list.empty.cta': 'Generate a document',
  'list.noResults.title': 'No documents match “{query}”',
  'list.noResults.filtered': 'No {status} documents in this view',
  'list.noResults.clear': 'Clear filters',
  'list.loadMore': 'Load more',
  'list.error.title': 'Couldn’t load your documents',
  'list.error.retry': 'Retry',
  'list.column.title': 'Title',
  'list.column.kind': 'Type',
  'list.column.status': 'Status',
  'list.column.difficulty': 'Difficulty',
  'list.column.updatedAt': 'Updated',
  'status.draft': 'Draft',
  'status.generating': 'Generating',
  'status.ready': 'Ready',
  'status.failed': 'Failed',
  'kind.book': 'Book',
  'kind.article': 'Article',
  'kind.report': 'Report',
  'difficulty.basic': 'Basic',
  'difficulty.intermediate': 'Intermediate',
  'difficulty.advanced': 'Advanced',
  'difficulty.mastery': 'Mastery',
  'detail.notFound.title': 'We couldn’t find this document.',
  'detail.notFound.cta': 'Back to documents',
  'detail.error.title': 'Couldn’t load this document.',
  'detail.error.retry': 'Retry',
  'detail.back': 'Back to documents',
  'detail.status': 'Status',
  'detail.language': 'Language',
  'detail.kind': 'Type',
  'detail.difficulty': 'Difficulty',
  'detail.currentVersion': 'Current version',
  'detail.noCurrentVersion': 'No version yet — generation is still in progress.',
  'detail.action.read': 'Read',
  'detail.action.edit': 'Edit',
  'detail.action.style': 'Themes & styling',
  'detail.action.share': 'Share',
  'detail.action.export': 'Export',
  'job.lastJob.title': 'Last job',
  'job.lastJob.complete': 'Job complete',
  'job.lastJob.artifacts': '{count, plural, =0 {No files rendered yet} one {# file rendered} other {# files rendered}}',
  'outline.title': 'Review the proposed structure',
  'outline.description': 'Confirm this structure before we draft the full document — you can rename chapters, edit summaries, reorder, and add or remove chapters first.',
  'outline.chapterSummaryLabel': 'Summary',
  'outline.chapterTitleLabel': 'Chapter title',
  'outline.moveUp': 'Move up',
  'outline.moveDown': 'Move down',
  'outline.removeChapter': 'Remove chapter',
  'outline.addChapter': 'Add chapter',
  'outline.newChapterTitle': 'New chapter',
  'outline.confirmAndGenerate': 'Confirm & generate',
  'outline.reject': 'Reject outline',
  'outline.reject.reasonLabel': 'Reason (optional)',
  'outline.reject.confirm': 'Reject',
  'outline.empty.validation': 'Add at least one chapter before confirming.',
  'outline.status.proposed': 'Outline proposed — awaiting your review',
  'outline.status.confirmed': 'Outline confirmed — drafting has started',
  'outline.status.rejected': 'Outline rejected',
  'outline.contentUnavailable.title': 'Outline ready to review',
  'outline.contentUnavailable.description':
    'The proposed structure is ready on the server, but this build can’t fetch its contents yet — reviewing chapter-by-chapter isn’t available here. You can still reject it below.',
  'versions.title': 'Version history',
  'versions.error': 'Couldn’t load version history.',
  'versions.empty': 'No versions yet.',
  'versions.origin.generate': 'Generated',
  'versions.origin.expand': 'Expanded',
  'versions.origin.cut_continue': 'Cut & continued',
  'versions.origin.difficulty_elevate': 'Difficulty changed',
  'versions.origin.translate': 'Translated',
  'versions.origin.edit': 'Edited',
  'versions.origin.restyle': 'Restyled',
  'versions.origin.imported': 'Imported',
  'artifacts.title': 'Deliverables',
  'artifacts.produced': 'An artifact was produced by this job — downloads become available once rendering ships.',
  'artifacts.empty': 'No deliverables yet.',
  'job.title': 'Generation in progress',
  'job.missing.title': 'Generation is in progress on another device or session',
  'job.missing.description': 'Live progress for this generation isn’t available here — it started elsewhere. It will finish in the background; check back for the new version.',
  'toast.outlineConfirmed.action': 'Confirmed',
  'toast.outlineRejected.action': 'Rejected',
} as const;

const ro: Record<keyof typeof en, string> = {
  'list.eyebrow': 'Bibliotecă',
  'list.title': 'Documente',
  'list.hint.label': 'Sfat:',
  'list.hint.text': 'apasă {shortcut} oriunde pentru a sări la un document sau a rula o acțiune.',
  'list.hint.dismiss': 'Nu mai arăta',
  'list.hint.close': 'Închide sfatul',
  'list.newDoc': 'Document nou',
  'list.searchLabel': 'Caută documente',
  'list.searchPlaceholder': 'Caută după titlu…',
  'list.sort.label': 'Sortează după',
  'list.sort.updatedAt-desc': 'Recent actualizate',
  'list.sort.createdAt-desc': 'Recent create',
  'list.sort.title-asc': 'Titlu A–Z',
  'list.sort.status-asc': 'Stare',
  'list.filter.label': 'Filtrează după stare',
  'list.filter.all': 'Toate',
  'list.filter.ready': 'Gata',
  'list.filter.generating': 'Se generează',
  'list.filter.draft': 'Ciorne',
  'list.empty.title': 'Niciun document încă',
  'list.empty.description': 'Generează, extinde și distribuie documente îmbunătățite cu AI — începe cu primul tău document.',
  'list.empty.cta': 'Generează un document',
  'list.noResults.title': 'Niciun document nu se potrivește cu „{query}”',
  'list.noResults.filtered': 'Niciun document „{status}” în această vizualizare',
  'list.noResults.clear': 'Șterge filtrele',
  'list.loadMore': 'Încarcă mai multe',
  'list.error.title': 'Nu am putut încărca documentele tale',
  'list.error.retry': 'Reîncearcă',
  'list.column.title': 'Titlu',
  'list.column.kind': 'Tip',
  'list.column.status': 'Stare',
  'list.column.difficulty': 'Dificultate',
  'list.column.updatedAt': 'Actualizat',
  'status.draft': 'Ciornă',
  'status.generating': 'Se generează',
  'status.ready': 'Gata',
  'status.failed': 'Eșuat',
  'kind.book': 'Carte',
  'kind.article': 'Articol',
  'kind.report': 'Raport',
  'difficulty.basic': 'De bază',
  'difficulty.intermediate': 'Intermediar',
  'difficulty.advanced': 'Avansat',
  'difficulty.mastery': 'Măiestrie',
  'detail.notFound.title': 'Nu am găsit acest document.',
  'detail.notFound.cta': 'Înapoi la documente',
  'detail.error.title': 'Nu am putut încărca acest document.',
  'detail.error.retry': 'Reîncearcă',
  'detail.back': 'Înapoi la documente',
  'detail.status': 'Stare',
  'detail.language': 'Limbă',
  'detail.kind': 'Tip',
  'detail.difficulty': 'Dificultate',
  'detail.currentVersion': 'Versiunea curentă',
  'detail.noCurrentVersion': 'Nicio versiune încă — generarea este în curs.',
  'detail.action.read': 'Citește',
  'detail.action.edit': 'Editează',
  'detail.action.style': 'Teme și stilizare',
  'detail.action.share': 'Distribuie',
  'detail.action.export': 'Exportă',
  'job.lastJob.title': 'Ultima sarcină',
  'job.lastJob.complete': 'Sarcină finalizată',
  'job.lastJob.artifacts':
    '{count, plural, =0 {Niciun fișier randat încă} one {# fișier randat} few {# fișiere randate} other {# de fișiere randate}}',
  'outline.title': 'Revizuiește structura propusă',
  'outline.description': 'Confirmă această structură înainte să redactăm documentul complet — poți redenumi capitole, edita rezumate, reordona și adăuga sau elimina capitole mai întâi.',
  'outline.chapterSummaryLabel': 'Rezumat',
  'outline.chapterTitleLabel': 'Titlul capitolului',
  'outline.moveUp': 'Mută în sus',
  'outline.moveDown': 'Mută în jos',
  'outline.removeChapter': 'Elimină capitolul',
  'outline.addChapter': 'Adaugă capitol',
  'outline.newChapterTitle': 'Capitol nou',
  'outline.confirmAndGenerate': 'Confirmă și generează',
  'outline.reject': 'Respinge structura',
  'outline.reject.reasonLabel': 'Motiv (opțional)',
  'outline.reject.confirm': 'Respinge',
  'outline.empty.validation': 'Adaugă cel puțin un capitol înainte de a confirma.',
  'outline.status.proposed': 'Structură propusă — în așteptarea revizuirii tale',
  'outline.status.confirmed': 'Structură confirmată — redactarea a început',
  'outline.status.rejected': 'Structură respinsă',
  'outline.contentUnavailable.title': 'Structura este gata de revizuit',
  'outline.contentUnavailable.description':
    'Structura propusă este gata pe server, dar această versiune nu poate încă să-i preia conținutul — revizuirea capitol cu capitol nu este disponibilă aici. O poți respinge mai jos.',
  'versions.title': 'Istoric versiuni',
  'versions.error': 'Nu am putut încărca istoricul versiunilor.',
  'versions.empty': 'Nicio versiune încă.',
  'versions.origin.generate': 'Generat',
  'versions.origin.expand': 'Extins',
  'versions.origin.cut_continue': 'Tăiat și continuat',
  'versions.origin.difficulty_elevate': 'Dificultate schimbată',
  'versions.origin.translate': 'Tradus',
  'versions.origin.edit': 'Editat',
  'versions.origin.restyle': 'Restilizat',
  'versions.origin.imported': 'Importat',
  'artifacts.title': 'Livrabile',
  'artifacts.produced': 'Un fișier a fost produs de această sarcină — descărcările vor fi disponibile odată ce randarea este lansată.',
  'artifacts.empty': 'Niciun livrabil încă.',
  'job.title': 'Generare în curs',
  'job.missing.title': 'Generarea este în curs pe alt dispozitiv sau sesiune',
  'job.missing.description': 'Progresul live pentru această generare nu este disponibil aici — a început în altă parte. Se va finaliza în fundal; revino pentru noua versiune.',
  'toast.outlineConfirmed.action': 'Confirmată',
  'toast.outlineRejected.action': 'Respinsă',
};

export const DOCUMENTS_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type DocumentsCatalogKey = keyof typeof en;
